// tslint:disable:no-submodule-imports
import moment, { Moment } from 'moment';

import * as React from 'react';
import { DateRangePicker as DatePicker } from 'react-dates';

import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import styled from 'styled-components';
import { DISPLAY_DATE_FORMAT } from '../../constants/dates';
import { Grey, MangoAnotherBlue, White } from '../../styles/colours';
import { DemiBold, Medium } from '../../styles/fonts';
import { StyledDatePickerPopUp } from './date-range-popup-styles';

type FocusProps = 'startDate' | 'endDate' | null;

export interface IDateProps {
  startDate?: Moment | null;
  endDate?: Moment | null;
}

interface IDateRangePickerProps {
  initialRange: IDateProps;
  onChange: (value: IDateProps) => void;
  anchorDirection: 'left' | 'right' | undefined;
}

const Presets = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0rem 1rem 0rem 1rem;
  button:focus {
    outline: 0;
    color: ${MangoAnotherBlue};
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  flex: 1;
  padding: 5rem 1rem;
  margin: 1rem -45.5rem -4rem 0rem;
`;

const StyledContainer = styled.div`
  border-right: 1px solid ${Grey};
  padding: 1rem;
`;

const CancelButton = styled.button`
  font-family: ${DemiBold};
  border: 1px solid ${Grey};
  border-radius: 3px;
  color: ${MangoAnotherBlue};
  background: transparent;
  padding: 0.75rem 2rem;
  margin-right: 0.5rem;
  cursor: pointer;
`;

const ApplyButton = styled.button`
  font-family: ${DemiBold};
  background: ${MangoAnotherBlue};
  color: ${White};
  border-radius: 3px;
  border: none;
  padding: 0.75rem 2rem;
  cursor: pointer;
`;

const Preset = styled.button`
  font-family: ${Medium};
  text-align: left;
  border: 0;
  white-space: nowrap;
  padding: 1rem 0.5rem;
  margin: 0.25rem;
  cursor: pointer;
  background: none;
`;

const DateRangePicker: React.FunctionComponent<IDateRangePickerProps> = ({
  onChange,
  initialRange,
  anchorDirection,
}) => {
  const [startDate, setStartDate] = React.useState<Moment | null>(
    initialRange.startDate || null
  );
  const [endDate, setEndDate] = React.useState<Moment | null>(
    initialRange.endDate || null
  );
  const [focusedInput, setFocusedInput] = React.useState<FocusProps>(null);

  const refreshDates = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const today = moment();
  const sevenDaysAgo = moment().subtract(7, 'day');
  const fourteenDaysAgo = moment().subtract(14, 'day');
  const thirtyDaysAgo = moment().subtract(30, 'day');
  const startOfMonth = moment().startOf('month');
  const startOfLastMonth = moment()
    .subtract(1, 'month')
    .startOf('month');
  const endOfLastMonth = moment()
    .subtract(1, 'month')
    .endOf('month');

  const presets = [
    {
      text: 'Last 7 days',
      start: sevenDaysAgo,
      end: today,
    },
    {
      text: 'Last 14 days',
      start: fourteenDaysAgo,
      end: today,
    },
    {
      text: 'Last 30 days',
      start: thirtyDaysAgo,
      end: today,
    },
    {
      text: 'This Month',
      start: startOfMonth,
      end: today,
    },
    {
      text: 'Last Month',
      start: startOfLastMonth,
      end: endOfLastMonth,
    },
  ];

  const applyDates = () => {
    onChange({ startDate, endDate });
    setFocusedInput(null);
  };

  const renderDatePresets = () => {
    return (
      <StyledContainer>
        <Presets>
          {presets.map(({ text, start, end }) => {
            return (
              <Preset
                key={text}
                type="button"
                onClick={() =>
                  updateWithPreset({ startDate: start, endDate: end })
                }
              >
                {text}
              </Preset>
            );
          })}
        </Presets>
        <ButtonContainer>
          <CancelButton
            data-type="cancel-button"
            onClick={() => setFocusedInput(null)}
          >
            Cancel
          </CancelButton>
          <ApplyButton data-type="apply-button" onClick={applyDates}>
            Apply
          </ApplyButton>
        </ButtonContainer>
      </StyledContainer>
    );
  };

  const updateDateData = async ({
    startDate: start = null,
    endDate: end = null,
  }: IDateProps) => {
    if (!focusedInput) {
      setStartDate(start);
      setEndDate(end);
    }

    if (focusedInput === 'startDate') {
      refreshDates();
      setStartDate(start);
      return;
    }
    if (focusedInput === 'endDate') {
      setEndDate(end);
      return;
    }
  };

  const updateWithPreset = async ({
    startDate: start = null,
    endDate: end = null,
  }: IDateProps) => {
    refreshDates();
    setStartDate(start);
    setEndDate(end);
  };

  const dateFormatSetting = localStorage.getItem('dateFormat');
  const displayDateFormat = dateFormatSetting
    ? dateFormatSetting
    : DISPLAY_DATE_FORMAT;

  return (
    <StyledDatePickerPopUp>
      <DatePicker
        isOutsideRange={() => false}
        calendarInfoPosition="before"
        renderCalendarInfo={renderDatePresets}
        minimumNights={0}
        startDatePlaceholderText="Start Date"
        endDatePlaceholderText="End Date"
        startDateId="startDate"
        endDateId="endDate"
        displayFormat={displayDateFormat}
        firstDayOfWeek={1}
        startDate={startDate}
        endDate={endDate}
        onDatesChange={updateDateData}
        focusedInput={focusedInput}
        onFocusChange={(input: FocusProps) => {
          setFocusedInput(input);
        }}
        readOnly={true}
        keepOpenOnDateSelect={true}
        openDirection="up"
        anchorDirection={anchorDirection}
      />
    </StyledDatePickerPopUp>
  );
};

export { DateRangePicker };
